import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Tournament } from '@swiss-magic/shared-types';
import { isSafeHttpsLink, mapPlaceName, urlHost } from '@/lib/safeUrl';
import type { TournamentLifecycle } from '../useTournamentLifecycle';

// Story 082/083 — tournament hub status & meta panel. Read-only meta;
// edits go through the Edit sidebar item. Delete lives here (draft
// only) as a destructive footer action so the whole lifecycle is
// reachable from the hub.

interface Props {
  tournament: Tournament;
  lifecycle: TournamentLifecycle;
  /** Story 093 — delete is owner-only. */
  isOwner: boolean;
  /** Story 107 — god mode may delete a tournament at any stage. */
  godMode?: boolean;
}

function statusLabel(status: string, t: ReturnType<typeof useTranslation>['t']): string {
  const key = `tournament_card.status.${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function systemLabel(system: string, t: ReturnType<typeof useTranslation>['t']): string {
  const key = `tournament_card.system.${system}`;
  const translated = t(key);
  return translated === key ? system : translated;
}

export function StatusPanel({ tournament, lifecycle, isOwner, godMode }: Props) {
  const { t } = useTranslation('director');

  return (
    <Card data-testid="tournament-hub-panel-status">
      <CardHeader>
        <CardTitle className="text-base">
          {t('tournament_hub.panels.status.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase text-muted-foreground">
              {t('tournament_hub.panels.status.phase')}
            </dt>
            <dd className="mt-1">
              <Badge variant="secondary">
                {statusLabel(tournament.status, t)}
              </Badge>
              {tournament.godModeUsed && (
                <Badge
                  variant="destructive"
                  className="ml-1"
                  data-testid="tournament-hub-god-mode-used"
                  title={t('tournament_hub.panels.status.god_mode_help')}
                >
                  {t('tournament_hub.panels.status.god_mode_used')}
                </Badge>
              )}
            </dd>
            {tournament.godModeUsed && (
              <p
                className="mt-1 text-xs text-muted-foreground"
                data-testid="tournament-hub-god-mode-help"
              >
                {t('tournament_hub.panels.status.god_mode_help')}
              </p>
            )}
          </div>
          {tournament.startDate && (
            <div>
              <dt className="text-xs uppercase text-muted-foreground">
                {t('tournament_hub.panels.status.start_date')}
              </dt>
              <dd className="mt-1">{tournament.startDate}</dd>
            </div>
          )}
          {(tournament.location || isSafeHttpsLink(tournament.venueMapUrl)) && (
            <div>
              <dt className="text-xs uppercase text-muted-foreground">
                {t('tournament_hub.panels.status.location')}
              </dt>
              <dd className="mt-1">
                {isSafeHttpsLink(tournament.venueMapUrl) ? (
                  <a
                    href={tournament.venueMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:text-primary/80"
                  >
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {tournament.location ||
                      mapPlaceName(tournament.venueMapUrl) ||
                      urlHost(tournament.venueMapUrl)}
                  </a>
                ) : (
                  tournament.location
                )}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-xs uppercase text-muted-foreground">
              {t('tournament_hub.panels.status.game')}
            </dt>
            <dd className="mt-1">
              {tournament.gameName || tournament.game}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-muted-foreground">
              {t('tournament_hub.panels.status.system')}
            </dt>
            <dd className="mt-1" data-testid="tournament-hub-system">
              {systemLabel(tournament.system, t)}
            </dd>
          </div>
          {tournament.maxPlayers != null && (
            <div>
              <dt className="text-xs uppercase text-muted-foreground">
                {t('tournament_hub.panels.status.max_players')}
              </dt>
              <dd className="mt-1">{tournament.maxPlayers}</dd>
            </div>
          )}
          {tournament.entryFee && (
            <div>
              <dt className="text-xs uppercase text-muted-foreground">
                {t('tournament_hub.panels.status.entry_fee')}
              </dt>
              <dd className="mt-1">{tournament.entryFee}</dd>
            </div>
          )}
          {isSafeHttpsLink(tournament.externalUrl) && (
            <div>
              <dt className="text-xs uppercase text-muted-foreground">
                {t('tournament_hub.panels.status.external_url')}
              </dt>
              <dd className="mt-1">
                <a
                  data-testid="tournament-hub-external-url"
                  href={tournament.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {tournament.externalUrlLabel || urlHost(tournament.externalUrl)}
                </a>
              </dd>
            </div>
          )}
        </dl>
        {(tournament.status === 'draft' || godMode) && isOwner && (
          <div className="mt-4 border-t pt-3">
            <Button
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive"
              data-testid="tournament-hub-delete-button"
              onClick={() => lifecycle.setDeleteOpen(true)}
            >
              {t('tournament_hub.panels.status.delete_tournament')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
